import { Injectable, TransferState, makeStateKey, StateKey, afterNextRender, signal } from '@angular/core';
import { MultilingualDropdownModel } from '../models/multilingual.model';

/**
 * Interface for user preferences that need to persist across SSR/hydration
 */
export interface UserPreferences {
    theme?: string;
    notifications?: boolean;
    [key: string]: any;
}

// Define state keys for data that needs to persist between SSR and hydration
const LANGUAGE_KEY = makeStateKey<string>('language');
const USER_PREFERENCES_KEY = makeStateKey<UserPreferences>('userPreferences');
const AVAILABLE_LANGUAGES_KEY = makeStateKey<MultilingualDropdownModel[]>('availableLanguages');

/**
 * Service for managing data transfer between server-side rendering (SSR) and client-side hydration.
 * 
 * This service wraps Angular's built-in TransferState to provide a type-safe, convenient API
 * for persisting application state across the SSR/hydration boundary. It's particularly useful
 * for maintaining user preferences, language settings, and other critical application state
 * that should be consistent between server and client rendering.
 * 
 * **SSR Considerations:**
 * - Uses afterNextRender() callback to detect client-side environment safely and only once
 * to prevent multiple re-initializations.
 * - Prevents redundant operations on every browser re-render through signal-based detection
 * - Only performs client-specific operations (like cleanup) after hydration is complete
 * - Safe for both server-side rendering and client-side hydration
 * 
 * @example
 * ```typescript
 * // In a component or service
 * constructor(private ssrStateService: SsrStateService) {}
 * 
 * // Set language during SSR
 * this.ssrStateService.setLanguage('en-US');
 * 
 * // Retrieve language during hydration
 * const language = this.ssrStateService.getLanguage();
 * ```
 * 
 * @see {@link https://angular.dev/api/core/TransferState} Angular TransferState Documentation
 */
@Injectable({
    providedIn: 'root'
})
export class SsrStateService {
    private readonly isClientSide = signal(false);

    constructor(
        private transferState: TransferState
    ) {
        // Consolidated client-side initialization.
        // This callback does not get called from server-side in SSR.
        afterNextRender(() => {
            this.isClientSide.set(true);
        });
    }

    /**
     * Generic method to get a value from transfer state
     * @template T The type of the value to retrieve
     * @param key The state key to retrieve
     * @param defaultValue The default value to return if key doesn't exist
     * @returns The stored value or default value
     */
    private getValue<T>(key: StateKey<T>, defaultValue: T): T {
        return this.transferState.get(key, defaultValue);
    }

    /**
     * Generic method to set a value in transfer state
     * @template T The type of the value to store
     * @param key The state key to store under
     * @param value The value to store
     */
    private setValue<T>(key: StateKey<T>, value: T): void {
        this.transferState.set(key, value);
    }

    /**
     * Generic method to check if a key exists in transfer state
     * @template T The type of the value
     * @param key The state key to check
     * @returns True if the key exists, false otherwise
     */
    private hasKey<T>(key: StateKey<T>): boolean {
        return this.transferState.hasKey(key);
    }

    /**
     * Generic method to remove a key from transfer state
     * 
     * **SSR Impact:**
     * - Only executes on client-side ONCE, after afterNextRender() callback.
     * (Instead of using afterRender(), which will proc as many times as the page 
     * would need to re-draw on init)
     * - Prevents server-side removal operations that could cause hydration mismatches
     * - Uses signal-based detection to avoid redundant operations on re-renders
     * 
     * @template T The type of the value
     * @param key The state key to remove
     */
    private removeKey<T>(key: StateKey<T>): void {
        if (this.isClientSide()) {
            this.transferState.remove(key);
        }
    }

    // Language management methods

    /**
     * Retrieves the current language code from transfer state
     * @returns The language code (e.g., 'en-US', 'zh-CN') or null if not set
     */
    getLanguage(): string | null {
        return this.getValue(LANGUAGE_KEY, null);
    }

    /**
     * Sets the current language code in transfer state
     * @param language The language code to store (e.g., 'en-US', 'zh-CN')
     */
    setLanguage(language: string): void {
        this.setValue(LANGUAGE_KEY, language);
    }

    /**
     * Checks if a language is stored in transfer state
     * @returns True if language is set, false otherwise
     */
    hasLanguage(): boolean {
        return this.hasKey(LANGUAGE_KEY);
    }

    /**
     * Removes the language from transfer state
     */
    removeLanguage(): void {
        this.removeKey(LANGUAGE_KEY);
    }

    // Available languages management methods

    /**
     * Retrieves the list of available languages from transfer state
     * @returns Array of available language options or empty array if not set
     */
    getAvailableLanguages(): MultilingualDropdownModel[] {
        return this.getValue(AVAILABLE_LANGUAGES_KEY, []);
    }

    /**
     * Sets the list of available languages in transfer state
     * @param languages Array of language options to store
     */
    setAvailableLanguages(languages: MultilingualDropdownModel[]): void {
        this.setValue(AVAILABLE_LANGUAGES_KEY, languages);
    }

    /**
     * Checks if available languages are stored in transfer state
     * @returns True if available languages are set, false otherwise
     */
    hasAvailableLanguages(): boolean {
        return this.hasKey(AVAILABLE_LANGUAGES_KEY);
    }

    /**
     * Removes the available languages from transfer state
     */
    removeAvailableLanguages(): void {
        this.removeKey(AVAILABLE_LANGUAGES_KEY);
    }

    // User preferences management methods

    /**
     * Retrieves user preferences from transfer state
     * @returns User preferences object or null if not set
     */
    getUserPreferences(): UserPreferences | null {
        return this.getValue(USER_PREFERENCES_KEY, null);
    }

    /**
     * Sets user preferences in transfer state
     * @param preferences User preferences object to store
     */
    setUserPreferences(preferences: UserPreferences): void {
        this.setValue(USER_PREFERENCES_KEY, preferences);
    }

    /**
     * Checks if user preferences are stored in transfer state
     * @returns True if user preferences are set, false otherwise
     */
    hasUserPreferences(): boolean {
        return this.hasKey(USER_PREFERENCES_KEY);
    }

    /**
     * Removes user preferences from transfer state
     */
    removeUserPreferences(): void {
        this.removeKey(USER_PREFERENCES_KEY);
    }

    // Utility methods

    /**
     * Cleans up all transfer state data after hydration is complete.
     * This is optional and should only be called when you're certain that
     * the data is no longer needed for the current session.
     * 
     * **SSR Impact:**
     * - Only executes on client-side ONCE, after afterNextRender() callback.
     * (Instead of using afterRender(), which will proc as many times as the page 
     * would need to re-draw on init)
     * - Prevents server-side cleanup operations during SSR
     * - Uses signal-based detection to ensure cleanup only happens once after hydration
     * - Safe to call multiple times - will only execute when client-side is detected
     * 
     * @param selective If true, only removes non-critical data. If false, removes all data.
     */
    cleanupAfterHydration(selective: boolean = true): void {
        if (this.isClientSide()) {
            if (selective) {
                // Only remove data that's typically not needed after initial hydration
                this.removeAvailableLanguages();
            }
            else {
                // Remove all transfer state data
                this.removeLanguage();
                this.removeAvailableLanguages();
                this.removeUserPreferences();
            }
        }
    }

    /**
     * Gets all stored keys for debugging purposes
     * @returns Array of key names that are currently stored
     */
    getStoredKeys(): string[] {
        const keys: string[] = [];
        if (this.hasLanguage()) keys.push('language');
        if (this.hasAvailableLanguages()) keys.push('availableLanguages');
        if (this.hasUserPreferences()) keys.push('userPreferences');
        return keys;
    }

    /**
     * Checks if the transfer state is empty
     * @returns True if no data is stored, false otherwise
     */
    isEmpty(): boolean {
        return this.transferState.isEmpty;
    }
}
